// The map's content (gate D2.2): the map row (content/tables/map.json) and the points of interest (pois.json), placed
// from the layout: at a placement, a region's centre, a landmark, a distance along a path, or a point. The map's
// picture (what the minimap and the map screen draw) is @rpg/world's map data in the map row's colours.
import { pointAlong, polyline, worldMap, type DiscoveryOptions, type PointOfInterest, type Shape, type World } from "@rpg/world";
import type { MapPicture } from "@rpg/ui";
import type { SpikeContent, ContentTables } from "../content";

/** The map row as the HUD reads it. */
export type MapRow = ContentTables["map"];

/** A point of interest with what the map and the HUD need beside the discovery's fields. */
export interface PoiInfo extends PointOfInterest {
  /** Its name (a string id). */
  name: string;
}

/** Everything the HUD reads for the map. */
export interface MapContent {
  row: MapRow;
  pois: PoiInfo[];
  discovery: DiscoveryOptions;
}

const centreOf = (s: Shape): [number, number] => {
  if (s.kind === "circle") return [s.x, s.z];
  if (s.kind === "rect") return [(s.x0 + s.x1) / 2, (s.z0 + s.z1) / 2];
  const n = s.points.length || 1;
  return [s.points.reduce((a, p) => a + p[0], 0) / n, s.points.reduce((a, p) => a + p[1], 0) / n];
};

/** Where a point of interest's `at` puts it on the layout. Throws naming what is missing. */
export function resolveAt(world: World, id: string, at: ContentTables["pois"]["at"]): [number, number] {
  const l = world.layout;
  const set = [at.placement, at.region, at.landmark, at.path, at.point].filter((x) => x !== undefined).length;
  if (set !== 1) throw new Error(`map: point of interest "${id}" needs exactly one of placement, region, landmark, path, point`);
  if (at.placement !== undefined) {
    const p = l.placements.find((q) => q.id === at.placement);
    if (!p) throw new Error(`map: point of interest "${id}": no placement "${at.placement}"`);
    return [p.pos[0], p.pos[1]];
  }
  if (at.region !== undefined) {
    const r = l.regions.find((q) => q.id === at.region);
    if (!r) throw new Error(`map: point of interest "${id}": no region "${at.region}"`);
    return centreOf(r.shape);
  }
  if (at.landmark !== undefined) {
    const m = l.landmarks.find((q) => q.id === at.landmark);
    if (!m) throw new Error(`map: point of interest "${id}": no landmark "${at.landmark}"`);
    return [m.x, m.z];
  }
  if (at.path !== undefined) {
    const p = l.paths.find((q) => q.id === at.path);
    if (!p) throw new Error(`map: point of interest "${id}": no path "${at.path}"`);
    const [x, z] = pointAlong(polyline(p.points), at.along ?? 0);
    return [x, z];
  }
  return [at.point![0]!, at.point![1]!];
}

/** Read the map's content for a world. */
export function readMapContent(spike: SpikeContent, world: World, id = "valley"): MapContent {
  const reg = spike.registry;
  const row = reg.get("map", id);
  if (!row) throw new Error(`map: no map row "${id}"`);
  const pois: PoiInfo[] = reg.rows("pois").map((r) => {
    const [x, z] = resolveAt(world, r.id, r.at);
    return { id: r.id, kind: r.kind, name: r.name.id, x, z, seeRange: r.see, reachRange: r.reach, ...(r.waypoint ? { waypoint: true } : {}) };
  });
  return { row: structuredClone(row), pois, discovery: { bounds: { ...world.layout.bounds }, cell: row.cell, revealRadius: row.revealRadius, pois } };
}

/** The map's picture of the world in the map row's colours: regions, rises, the water, the paths, the buildings and
 *  camps as blocks. */
export function mapPicture(world: World, row: MapRow): MapPicture {
  const m = worldMap(world.layout);
  const c = row.colours;
  const areas: MapPicture["areas"] = [];
  for (const l of m.landmarks) if (l.kind === "rise" && c.rise) areas.push({ shape: { kind: "circle", x: l.x, z: l.z, r: l.radius * 0.6 }, fill: c.rise });
  for (const r of m.regions) {
    const fill = c.regions[r.id];
    if (fill) areas.push({ shape: r.shape, fill });
  }
  const lines: MapPicture["lines"] = [];
  for (const w of m.water) {
    if (w.kind === "pond" && w.shape) areas.push({ shape: w.shape, fill: c.water });
    if (w.kind === "stream" && w.points) lines.push({ points: w.points, width: (w.width ?? 2) + 1, colour: c.water });
  }
  for (const p of m.paths) lines.push({ points: p.points, width: Math.max(2, p.width), colour: c.path });
  const blocks: NonNullable<MapPicture["blocks"]> = [];
  for (const p of m.placements) {
    if (p.kind === "building") blocks.push({ x: p.pos[0], z: p.pos[1], half: p.piece.includes("bridge") ? 1.4 : 3.2, yaw: p.yaw, colour: c.building });
    if (p.kind === "camp") blocks.push({ x: p.pos[0], z: p.pos[1], half: 2, yaw: p.yaw, colour: c.camp });
  }
  return { bounds: m.bounds, ground: c.ground, areas, lines, blocks };
}
